import { Component, ChangeDetectionStrategy, inject, signal, OnInit, computed } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TabsModule } from 'primeng/tabs';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { SelectModule } from 'primeng/select';
import { ToastrService } from 'ngx-toastr';
import { finalize, startWith } from 'rxjs';
import { UserService, UserDTO, ChangePasswordRequest, UploadAvatarRequest } from '../../services/user.service';
import { EmailSignatureService, type EmailSignatureDTO } from '../../services/email-signature.service';
import { AuthService } from '../../services/auth.service';
import { apiFailureMessage, httpErrorApiMessage } from '../../core/api-response-utils';
import { AvatarUploaderComponent } from '../shared/avatar-uploader/avatar-uploader.component';
import type { AvatarCropResult } from '../shared/avatar-uploader/avatar-crop.model';

export interface PasswordRequirement {
  id: 'minLength' | 'uppercase' | 'number' | 'special' | 'match';
  label: string;
  met: boolean;
}

function passwordMatchValidator(passwordKey: string, confirmKey: string): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const password = group.get(passwordKey)?.value;
    const confirm = group.get(confirmKey)?.value;
    if (!password || !confirm) {
      return null;
    }
    return password === confirm ? null : { passwordMismatch: true };
  };
}

function strongPasswordValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = String(control.value ?? '');
    if (!value) {
      return null;
    }
    const errors: ValidationErrors = {};
    if (value.length < 8) {
      errors['minLength'] = true;
    }
    if (!/[A-Z]/.test(value)) {
      errors['uppercase'] = true;
    }
    if (!/[0-9]/.test(value)) {
      errors['number'] = true;
    }
    if (!/[^A-Za-z0-9]/.test(value)) {
      errors['special'] = true;
    }
    return Object.keys(errors).length ? { strongPassword: errors } : null;
  };
}

function evaluatePasswordRequirements(password: string, confirmPassword: string): PasswordRequirement[] {
  return [
    {
      id: 'minLength',
      label: 'Al menos 8 caracteres',
      met: password.length >= 8
    },
    {
      id: 'uppercase',
      label: 'Al menos una letra mayúscula',
      met: /[A-Z]/.test(password)
    },
    {
      id: 'number',
      label: 'Al menos un número',
      met: /[0-9]/.test(password)
    },
    {
      id: 'special',
      label: 'Al menos un carácter especial',
      met: /[^A-Za-z0-9]/.test(password)
    },
    {
      id: 'match',
      label: 'Las contraseñas coinciden',
      met: password.length > 0 && confirmPassword.length > 0 && password === confirmPassword
    }
  ];
}

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    FloatLabelModule,
    ButtonModule,
    CardModule,
    TabsModule,
    InputGroupModule,
    InputGroupAddonModule,
    SelectModule,
    AvatarUploaderComponent
  ]
})
export class ProfileComponent implements OnInit {
  private fb = inject(FormBuilder);
  private userService = inject(UserService);
  private emailSignatureService = inject(EmailSignatureService);
  private authService = inject(AuthService);
  private toastr = inject(ToastrService);

  activeTab = signal<string>('generales');
  loading = signal(false);
  passwordLoading = signal(false);
  avatarUploading = signal(false);
  signatureSaving = signal(false);
  profile = signal<UserDTO | null>(null);
  avatarPreviewUrl = signal<string | null>(null);
  signatures = signal<EmailSignatureDTO[]>([]);
  selectedSignatureId = signal<number | null>(null);

  signatureOptions = computed(() => [
    { label: 'Sin firma', value: null as number | null },
    ...this.signatures().map((s) => ({ label: s.name, value: s.id as number | null }))
  ]);

  currentPasswordVisible = signal(false);
  newPasswordVisible = signal(false);
  confirmPasswordVisible = signal(false);

  passwordRequirements = signal<PasswordRequirement[]>(evaluatePasswordRequirements('', ''));
  allPasswordRequirementsMet = computed(() => this.passwordRequirements().every((r) => r.met));

  tabs = [
    { value: 'generales', label: 'Generales', icon: 'pi pi-user' },
    { value: 'password', label: 'Contraseña', icon: 'pi pi-lock' }
  ] as const;

  passwordForm: FormGroup = this.fb.group(
    {
      currentPassword: ['', [Validators.required, Validators.maxLength(50)]],
      newPassword: ['', [Validators.required, Validators.maxLength(50), strongPasswordValidator()]],
      confirmPassword: ['', [Validators.required, Validators.maxLength(50)]]
    },
    { validators: passwordMatchValidator('newPassword', 'confirmPassword') }
  );

  constructor() {
    this.passwordForm.valueChanges
      .pipe(
        startWith(this.passwordForm.getRawValue()),
        takeUntilDestroyed()
      )
      .subscribe((value) => {
        this.passwordRequirements.set(
          evaluatePasswordRequirements(
            String(value?.newPassword ?? ''),
            String(value?.confirmPassword ?? '')
          )
        );
      });
  }

  ngOnInit(): void {
    this.loadProfile();
    this.loadSignatures();
  }

  onTabChange(value: string | number | undefined): void {
    if (value == null) return;
    this.activeTab.set(String(value));
  }

  loadProfile(): void {
    this.loading.set(true);
    this.userService
      .getMe()
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res) => {
          if (res.ok && res.body) {
            this.profile.set(res.body);
            this.avatarPreviewUrl.set(res.body.avatarUrl || null);
            this.authService.setProfileAvatarUrl(res.body.avatarUrl);
            this.selectedSignatureId.set(res.body.signatureId ?? null);
          } else {
            this.toastr.error(apiFailureMessage(res, 'No se pudo cargar el perfil'));
          }
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al cargar el perfil'));
        }
      });
  }

  loadSignatures(): void {
    this.emailSignatureService.list().subscribe({
      next: (res) => {
        this.signatures.set(res.ok && res.body ? res.body : []);
      },
      error: () => {
        this.signatures.set([]);
      }
    });
  }

  onSignatureChange(signatureId: number | null): void {
    if (this.signatureSaving()) {
      return;
    }
    this.selectedSignatureId.set(signatureId);
    this.signatureSaving.set(true);
    this.userService
      .updateOwnSignature(signatureId)
      .pipe(finalize(() => this.signatureSaving.set(false)))
      .subscribe({
        next: (res) => {
          if (res.ok && res.body) {
            this.profile.set(res.body);
            this.selectedSignatureId.set(res.body.signatureId ?? null);
            this.toastr.success(
              signatureId == null ? 'Firma por defecto eliminada' : 'Firma por defecto actualizada'
            );
          } else {
            this.toastr.error(apiFailureMessage(res, 'No se pudo guardar la firma por defecto'));
            this.selectedSignatureId.set(this.profile()?.signatureId ?? null);
          }
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al guardar la firma por defecto'));
          this.selectedSignatureId.set(this.profile()?.signatureId ?? null);
        }
      });
  }

  fullName(): string {
    const p = this.profile();
    if (!p) return '';
    return [p.name, p.lastname, p.surname].filter(Boolean).join(' ').trim();
  }

  onAvatarChange(result: AvatarCropResult): void {
    if (this.avatarUploading()) return;

    const request: UploadAvatarRequest = {
      avatarBase64: result.base64,
      avatarContentType: result.contentType
    };

    this.avatarPreviewUrl.set(result.previewUrl);
    this.avatarUploading.set(true);
    this.userService
      .updateOwnAvatar(request)
      .pipe(finalize(() => this.avatarUploading.set(false)))
      .subscribe({
        next: (res) => {
          if (res.ok && res.body) {
            this.profile.set(res.body);
            this.avatarPreviewUrl.set(res.body.avatarUrl || result.previewUrl);
            this.authService.setProfileAvatarUrl(res.body.avatarUrl);
            this.toastr.success('Foto de perfil actualizada');
          } else {
            this.toastr.error(apiFailureMessage(res, 'No se pudo actualizar la foto'));
            this.avatarPreviewUrl.set(this.profile()?.avatarUrl || null);
          }
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al subir la foto de perfil'));
          this.avatarPreviewUrl.set(this.profile()?.avatarUrl || null);
        }
      });
  }

  toggleCurrentPasswordVisible(): void {
    this.currentPasswordVisible.update((v) => !v);
  }

  toggleNewPasswordVisible(): void {
    this.newPasswordVisible.update((v) => !v);
  }

  toggleConfirmPasswordVisible(): void {
    this.confirmPasswordVisible.update((v) => !v);
  }

  onChangePassword(): void {
    if (this.passwordForm.invalid || this.passwordLoading()) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    const value = this.passwordForm.getRawValue();
    const request: ChangePasswordRequest = {
      currentPassword: value.currentPassword,
      newPassword: value.newPassword,
      confirmPassword: value.confirmPassword
    };

    this.passwordLoading.set(true);
    this.userService
      .changeOwnPassword(request)
      .pipe(finalize(() => this.passwordLoading.set(false)))
      .subscribe({
        next: (res) => {
          if (res.ok) {
            this.toastr.success('Contraseña actualizada correctamente');
            this.passwordForm.reset({
              currentPassword: '',
              newPassword: '',
              confirmPassword: ''
            });
            this.currentPasswordVisible.set(false);
            this.newPasswordVisible.set(false);
            this.confirmPasswordVisible.set(false);
          } else {
            this.toastr.error(apiFailureMessage(res, 'No se pudo actualizar la contraseña'));
          }
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al actualizar la contraseña'));
        }
      });
  }
}
